import React, { Component } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import InventoryAPI from '../../../services/inventoryService';
import { getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';
import { formatProductLabel } from '../../../util/productLabel';

/**
 * TransferInventoryModal - Transfer an inventory item between locations
 *
 * Props:
 * - show: Boolean
 * - onHide: Function
 * - onSuccess: Function - called after successful transfer (reloads data)
 * - item: Object - inventory item { item_id, barcode, quantity_remaining, product_name, brand }
 * - locations: Array - all company locations
 * - currentLocationId: string|number - the location the item is currently at
 * - installerId: number - installer ID
 */
class TransferInventoryModal extends Component {
  state = {
    targetLocationId: '',
    quantity: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    if (this.props.show && !prevProps.show) {
      const qty = parseFloat(this.props.item?.quantity_remaining || 0);
      this.setState({
        targetLocationId: '',
        quantity: qty > 0 ? qty : '',
        loading: false,
        error: null
      });
    }
  }

  handleSubmit = async () => {
    const { item, currentLocationId, installerId, onSuccess, onHide } = this.props;
    const { targetLocationId, quantity } = this.state;

    if (!targetLocationId) {
      this.setState({ error: 'Please select a destination location.' });
      return;
    }

    const qty = parseFloat(quantity);
    if (!qty || qty <= 0) {
      this.setState({ error: 'Please enter a valid quantity.' });
      return;
    }

    const maxQty = parseFloat(item.quantity_remaining || 0);
    if (qty > maxQty) {
      this.setState({ error: `Quantity cannot exceed ${maxQty.toFixed(2)} (amount remaining).` });
      return;
    }

    try {
      this.setState({ loading: true, error: null });
      await InventoryAPI.transferInventory({
        itemId: item.item_id || item.id,
        fromLocationId: currentLocationId,
        toLocationId: targetLocationId,
        quantity: qty,
        installerId
      });
      onHide();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Transfer error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to transfer inventory.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  render() {
    const { show, onHide, item, locations, currentLocationId } = this.props;
    const { targetLocationId, quantity, loading, error } = this.state;

    if (!item) return null;

    const currentLoc = locations?.find(l => String(l.location_id) === String(currentLocationId));
    const otherLocations = (locations || []).filter(l => String(l.location_id) !== String(currentLocationId));
    const displayName = formatProductLabel(item);

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-arrow-right-arrow-left text-primary mr-2"></i>
            Transfer Inventory
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3" style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: '6px', borderLeft: '4px solid #3FB2BE' }}>
            <strong>{displayName}</strong>
            {item.barcode && <div className="mt-1"><code>{item.barcode}</code></div>}
            <div className="text-muted small">
              Available: {parseFloat(item.quantity_remaining || 0).toFixed(2)}
            </div>
          </div>

          {/* Current Location */}
          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold">From Location</Form.Label>
            <div>
              {currentLoc ? (
                <span
                  className="badge"
                  style={{
                    backgroundColor: getLocationBadgeColor(currentLocationId, locations),
                    color: '#fff',
                    fontSize: '0.9rem',
                    padding: '6px 10px'
                  }}
                >
                  {getLocationName(currentLoc)}
                </span>
              ) : (
                <span className="text-muted">Unknown</span>
              )}
            </div>
          </Form.Group>

          {/* Destination */}
          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold">
              To Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="select"
              value={targetLocationId}
              onChange={(e) => this.setState({ targetLocationId: e.target.value, error: null })}
              disabled={loading}
            >
              <option value="">Select a location...</option>
              {otherLocations.map(l => (
                <option key={l.location_id} value={l.location_id}>
                  {getLocationName(l)}
                </option>
              ))}
            </Form.Control>
          </Form.Group>

          {/* Quantity */}
          <Form.Group className="mb-3">
            <Form.Label className="font-weight-bold">
              Quantity <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0.01"
              max={item.quantity_remaining || 0}
              value={quantity}
              onChange={(e) => this.setState({ quantity: e.target.value, error: null })}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              Max: {parseFloat(item.quantity_remaining || 0).toFixed(2)}
            </Form.Text>
          </Form.Group>

          {error && (
            <div className="alert alert-danger mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={this.handleSubmit}
            disabled={loading || !targetLocationId || !quantity}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Transferring...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-arrow-right-arrow-left mr-2"></i>
                Transfer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default TransferInventoryModal;
